import React from 'react';
import { CalendarClock, MapPin, Pause, Pencil, Play, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { StatusBadge, itemsSummary, policyLabel, prettyDate } from './recurringShared';

// One row in the "My schedules" list.
export const ScheduleCard = ({ template, canPlaceOrders, onEdit, onPause, onResume, onCancel }) => {
  const t = template;
  const isCancelled = t.status === 'cancelled';

  return (
    <Card data-testid={`schedule-card-${t.id}`} className={isCancelled ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold" data-testid={`schedule-cadence-${t.id}`}>
                {t.cadence_label}
              </span>
              <StatusBadge status={t.status} testId={`schedule-status-${t.id}`} />
              {t.next_delivery_date && !isCancelled && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" />
                  Next: {prettyDate(t.next_delivery_date)}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-800 mt-1.5 truncate" data-testid={`schedule-items-${t.id}`}>
              {itemsSummary(t.items)}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              {t.location_name && (
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{t.location_name}</span>
              )}
              <span data-testid={`schedule-policy-${t.id}`}>{policyLabel(t)}</span>
            </div>
          </div>

          {canPlaceOrders && !isCancelled && (
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => onEdit(t)} data-testid={`schedule-edit-btn-${t.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
              {t.status === 'active' ? (
                <Button variant="outline" size="sm" onClick={() => onPause(t)} data-testid={`schedule-pause-btn-${t.id}`}>
                  <Pause className="h-3.5 w-3.5 mr-1" /> Pause
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => onResume(t)} data-testid={`schedule-resume-btn-${t.id}`}>
                  <Play className="h-3.5 w-3.5 mr-1" /> Resume
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" data-testid={`schedule-cancel-btn-${t.id}`}>
                    <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="schedule-cancel-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel this recurring schedule?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Future unplaced dates will be skipped; already-placed orders are not affected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="schedule-cancel-keep-btn">Keep schedule</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => onCancel(t)}
                      data-testid="schedule-cancel-confirm-btn"
                    >
                      Cancel schedule
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
